import React, { useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { DataGridCustomPagination, dateFormatter } from "components/helpers";
import { useUserData } from "components/customHooks/userDataContext";
import { getUsers } from "apiHandler/actions";
import UsersAction from "components/modals/usersAction";
import { getFormattedDate } from "utils/helper";
import UserDetails from "components/modals/userDetails";
import UserAdvanceSearch from "components/modals/userAdvanceSearch.";
import ResetPasswordModal from "components/modals/resetPassword";

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [resetUser, setResetUser] = useState(null);

  const handleOpenResetPassword = (user) => {
    setResetUser(user);
    setOpenReset(true);
  };

  const handleCloseResetPassword = () => {
    setOpenReset(false);
    setResetUser(null);
  };

  const handleOpenDetailsModal = (user) => {
    setSelectedUser(user);
    setOpenDetails(true);
  };

  const handleCloseDetailsModal = () => {
    setOpenDetails(false);
    setSelectedUser(null);
  };

  const userData = useUserData();

  const token = userData?.access_token;

  const [form, setForm] = useState({
    user_id: "",
    user_name: "",
    first_name: "",
    last_name: "",
    password: "",
    user_role: "Staff",
    user_status: "Active",
    phone: "",
    practice_id_list: [],
    access_list: ["general"],
  });

  useEffect(() => {
    getUsersList();
  }, []);

  const getUsersList = async () => {
    setLoading(true);
    try {
      const payload = { practice_id: userData?.selected_practice };
      const res = await getUsers(
        userData?.user_details?.user_role === "Admin" ? {} : payload,
        token
      );
      console.log("res: ", res);
      if (res) {
        setUsers(res);
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setForm({
      user_id: "",
      user_name: "",
      first_name: "",
      last_name: "",
      password: "",
      user_role: "Staff",
      user_status: "Active",
      phone: "",
      practice_id_list: [],
      access_list: ["general"],
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const columns = [
    {
      field: "viewDetails",
      headerName: "Details",
      width: 120,
      renderCell: (params) => (
        <Button
          size="small"
          variant="text"
          onClick={() => handleOpenDetailsModal(params.row)}
        >
          View
        </Button>
      ),
    },
    { field: "user_id", headerName: "Email", width: 250 },
    {
      field: "first_name",
      headerName: "Name",
      width: 200,
      renderCell: (e) => <div>{`${e.value} ${e.row.last_name}`}</div>,
    },
    { field: "user_name", headerName: "User Name", width: 200 },
    { field: "phone_number", headerName: "Phone Number", width: 200 },
    {
      field: "created_date",
      headerName: "Created Date",
      width: 200,
      valueFormatter: (value) => (value ? getFormattedDate(value) : ""),
      valueGetter: (value) => dateFormatter(value),
    },
    { field: "user_role", headerName: "Role", width: 120 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "resetPassword",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          color="warning"
          onClick={() => handleOpenResetPassword(params.row)}
        >
          Reset Password
        </Button>
      ),
    },
  ];

  return (
    <Box p={2} bgcolor={"white"}>
      <Typography variant="h6" gutterBottom>
        Users
      </Typography>
      <Box
        mb={2}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"flex-end"}
        gap={2}
      >
        <Button variant="outlined" onClick={() => handleOpen()}>
          Add User
        </Button>
        <Button variant="outlined" onClick={() => setShowSearch(true)}>
          Advance Search
        </Button>
      </Box>

      <DataGrid
        rows={users}
        columns={columns}
        pageSize={10}
        autoHeight
        slots={{ pagination: DataGridCustomPagination, toolbar: GridToolbar }}
        loading={loading}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "linear-progress",
          },
        }}
      />

      <UserDetails
        openDetails={openDetails}
        handleCloseDetailsModal={handleCloseDetailsModal}
        selectedUser={selectedUser}
        fetchUsers={getUsersList}
      />
      <UsersAction
        form={form}
        setForm={setForm}
        setUsers={setUsers}
        handleClose={handleClose}
        open={open}
        userData={userData}
        fetchUsers={getUsersList}
      />
      <UserAdvanceSearch
        open={showSearch}
        handleClose={() => setShowSearch(false)}
      />
      <ResetPasswordModal
        open={openReset}
        onClose={handleCloseResetPassword}
        user={resetUser}
      />
    </Box>
  );
};

export default UsersList;
